import type { Account } from '@interfaces/account';
import type { Affiliation } from '@interfaces/affiliation';
import { AffiliationTypes } from '@interfaces/affiliationTypes';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { Box } from '@ovComponents/1-primative/box/box';
import { Icon } from '@ovComponents/1-primative/icon/icon';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { Card, CardContent } from '@ovComponents/2-component/card/card';
import { IconButton } from '@ovComponents/2-component/iconButton/iconButton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@ovComponents/2-component/table/table';
import { GoalLinkingReview } from '@ovComponents/4-module/workflowCompletion/subSteps/account/goalLinking';
import { colors } from '@theme/colors';
import { generateClientNameString } from '@utils/generateClientNameString';
import { useTranslation } from 'react-i18next';

type TfsaReviewItemProps = {
  account: Account;
  onDeleteAccount: (account?: Account) => void;
};
export const RRSPReviewItem = ({
  account,
  onDeleteAccount,
}: TfsaReviewItemProps) => {
  const { t } = useTranslation([
    'workflowCompletions',
    'affiliationRelationship',
  ]);
  const hasBeneficiary = (): boolean => {
    const response = account.affiliations?.find(
      (item) => item.type !== AffiliationTypes.Successor,
    );
    return !!response;
  };
  const getBeneficiary = (): Affiliation[] =>
    account.affiliations?.filter(
      (item) => item.type !== AffiliationTypes.Successor,
    ) ?? [];
  return (
    <Card variant='outlined' sx={{ mb: 2 }}>
      <CardContent>
        <Box
          pl={0.9}
          display='flex'
          flexDirection='row'
          alignItems='center'
          justifyContent='space-between'
        >
          <Typography variant='bodyLarge' weight='bold' sx={{ mt: 1 }}>
            {t('workflowCompletions:accountReviewPanel.rrsp')}
          </Typography>
          {account.id ? undefined : (
            <IconButton onClick={() => onDeleteAccount()}>
              <Icon icon={CloseRoundedIcon} size='small' />
            </IconButton>
          )}
        </Box>
        <Box
          sx={{ mt: 1, mb: 1, px: 1 }}
          display='flex'
          flexDirection='row'
          justifyContent='space-between'
        >
          <Typography variant='bodyMedium' weight='bold'>
            {t('workflowCompletions:accountReviewPanel.beneficiary')}
          </Typography>
          <Typography variant='labelLarge'>
            {hasBeneficiary() ? 'Yes' : 'No'}
          </Typography>
        </Box>
        {hasBeneficiary() ? (
          <Table
            sx={{
              tableLayout: 'fixed',
              borderCollapse: 'separate',
              borderSpacing: '2px',
            }}
            aria-label='table'
          >
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    backgroundColor: '#F0F4F8',
                    borderRadius: '2px',
                    border: 'none',
                    padding: '8px 5px',
                  }}
                  width='40%'
                >
                  <Typography
                    sx={{ color: colors.gray500 }}
                    variant='headingOverline'
                  >
                    {t(
                      'workflowCompletions:accountReviewPanel.tableItems.name',
                    )}
                  </Typography>
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: '#F0F4F8',
                    borderRadius: '2px',
                    border: 'none',
                    padding: '8px 5px',
                  }}
                  width='40%'
                >
                  <Typography
                    sx={{ color: colors.gray500 }}
                    variant='headingOverline'
                  >
                    {t(
                      'workflowCompletions:accountReviewPanel.tableItems.relationship',
                    )}
                  </Typography>
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: '#F0F4F8',
                    borderRadius: '2px',
                    border: 'none',
                    padding: '8px 5px',
                  }}
                  width='20%'
                >
                  <Typography
                    sx={{ color: colors.gray500 }}
                    variant='headingOverline'
                  >
                    {t(
                      'workflowCompletions:accountReviewPanel.tableItems.allocation',
                    )}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {getBeneficiary().map((item, index) => (
                <TableRow
                  hover
                  key={index}
                  sx={{
                    '&:last-child td, &:last-child th': { border: 0 },
                    textDecoration: 'none',
                  }}
                >
                  <TableCell sx={{ padding: '8px 5px' }}>
                    {generateClientNameString(item.user)}
                  </TableCell>
                  <TableCell sx={{ padding: '8px 5px' }}>
                    {t(
                      `affiliationTypes:affiliationRelationship.${item.relation}`,
                    )}
                  </TableCell>
                  <TableCell sx={{ padding: '8px 5px' }}>
                    {t(`${item.allocation}%`)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : undefined}
        <Box
          sx={{ mt: 1, px: 1 }}
          display='flex'
          flexDirection='row'
          justifyContent='space-between'
        >
          <GoalLinkingReview account={account} />
        </Box>
      </CardContent>
    </Card>
  );
};

export default RRSPReviewItem;
